import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useEsignRequestDeeplink } from "@/pages/esign/use-esign-request-deeplink.hook.ts";
import { PreboardingCard } from "@/pages/preboarding/components/PreboardingCard.tsx";
import { PreboardingEsignRow } from "@/pages/preboarding/components/PreboardingEsignRow.tsx";
import { PreboardingManagerCard } from "@/pages/preboarding/components/PreboardingManagerCard.tsx";
import { PreboardingProfileHeader } from "@/pages/preboarding/components/PreboardingProfileHeader.tsx";
import { PreboardingSection } from "@/pages/preboarding/components/PreboardingSection.tsx";
import { PreboardingSurveyRow } from "@/pages/preboarding/components/PreboardingSurveyRow.tsx";
import { PreboardingTodoRow } from "@/pages/preboarding/components/PreboardingTodoRow.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { usePreboardingHome } from "@/queries/preboarding/use-preboarding.query.ts";
import { PortalAccessMode } from "@/types/employees/employee.types.ts";
import { Button, EmptyStateBase, Page, Skeleton, Stack, Text, Title } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

function getGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "common:good_morning";
  if (hour >= 12 && hour < 17) return "common:good_afternoon";
  if (hour >= 17 && hour < 22) return "common:good_evening";
  return "common:good_night";
}

export function PreboardingHome() {
  const { t } = useTranslation();
  const { getDaysToDate } = useFormatter();
  const { data: employeeMe } = useEmployeeMe();
  const { data: home, isLoading, isError, isFetching, refetch } = usePreboardingHome();

  useEsignRequestDeeplink();

  const title =
    home?.portal_access_mode === PortalAccessMode.onboarding
      ? t("core:onboarding_home_title")
      : t("common:welcome");

  useTitle(title);

  const greeting = employeeMe ? `${t(getGreetingKey())}, ${employeeMe.first_name}` : undefined;

  const company = employeeMe?.organization.title ?? "";
  const daysUntilHire = employeeMe?.date_hired_on ? getDaysToDate(employeeMe.date_hired_on) : null;

  const noTasks =
    !!home &&
    home.surveys.length === 0 &&
    home.esign_requests.length === 0 &&
    home.tasks.length === 0;

  const journeyHint = noTasks
    ? t("core:preboarding_journey_done")
    : t("core:preboarding_journey_hint");
  const welcomeSubtitle =
    daysUntilHire !== null && daysUntilHire > 0
      ? `${t("core:preboarding_journey", { company, days: daysUntilHire })} ${journeyHint}`
      : journeyHint;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack gap={"sm"}>
          <Skeleton className={"h-16 w-full"} />
          <Skeleton className={"h-16 w-full"} />
          <Skeleton className={"h-16 w-full"} />
        </Stack>
      );
    }

    if (isError || !home) {
      return (
        <EmptyStateBase
          emoji={":warning:"}
          title={t("core:preboarding_error_title")}
          primaryButton={
            <Button variant={"secondary"} isLoading={isFetching} onClick={() => refetch()}>
              {t("common:retry")}
            </Button>
          }
        />
      );
    }

    const hasSurveys = home.surveys.length > 0;
    const hasEsign = home.esign_requests.length > 0;
    const hasTasks = home.tasks.length > 0;

    if (!hasSurveys && !hasEsign && !hasTasks) {
      return (
        <EmptyStateBase
          emoji={":supervillain:"}
          title={t("core:preboarding_empty_title")}
          description={t("core:preboarding_empty_description")}
        />
      );
    }

    return (
      <Stack gap={"xl"}>
        {hasSurveys && (
          <PreboardingSection title={t("core:preboarding_surveys_title")}>
            {home.surveys.map((survey) => (
              <PreboardingCard key={survey.survey.id}>
                <PreboardingSurveyRow survey={survey} />
              </PreboardingCard>
            ))}
          </PreboardingSection>
        )}
        {hasEsign && (
          <PreboardingSection title={t("core:preboarding_esign_title")}>
            {home.esign_requests.map((request) => (
              <PreboardingCard key={request.public_id}>
                <PreboardingEsignRow esignRequest={request} />
              </PreboardingCard>
            ))}
          </PreboardingSection>
        )}
        {hasTasks && (
          <PreboardingSection title={t("core:preboarding_tasks_title")}>
            {home.tasks.map((task) => (
              <PreboardingCard key={task.id}>
                <PreboardingTodoRow task={task} />
              </PreboardingCard>
            ))}
          </PreboardingSection>
        )}
      </Stack>
    );
  };

  return (
    <Page>
      <Page.Header title={greeting} />
      <Page.Content>
        <Stack className={"mx-auto my-6 w-full max-w-221"}>
          {employeeMe && <PreboardingProfileHeader employee={employeeMe} green={noTasks} />}
          {employeeMe && (
            <Stack gap={"sm"} className={"relative z-10"}>
              <Title header={t("core:preboarding_welcome", { company })} size={"lg"} paddingTop />
              <Text size={"3xl"} className={"text-secondary"}>
                {welcomeSubtitle}
              </Text>
            </Stack>
          )}
          <Title
            className={"relative z-10"}
            size="lg"
            header={t("tasks:things_to_do")}
            paddingBottom
            paddingTop
          />
          <Stack className="relative z-10 rounded-xs p-4 bg-dark-5 shadow-sm" gap="md">
            {renderContent()}
          </Stack>
          {home?.manager && (
            <>
              <Title
                className={"relative z-10"}
                header={t("common:my_team")}
                size={"lg"}
                paddingTop
                paddingBottom
              />
              <Stack direction={"horizontal"} gap={"md"} className={"relative z-10"}>
                <PreboardingManagerCard manager={home.manager} />
              </Stack>
            </>
          )}
        </Stack>
      </Page.Content>
    </Page>
  );
}
